import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { ConfirmationService } from 'primeng/api';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { IoTApiService, IoTDeviceCommandRecord, IoTDeviceRecord } from './iot-api.service';

const SENSITIVE_COMMAND_TYPES = new Set(['reboot', 'factory_reset', 'firmware_update', 'remote_wipe', 'config_push']);

@Component({
  standalone: true,
  selector: 'ain-iot-device-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    AinDatePipe,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    ConfirmDialogModule,
    RouterLink
  ],
  providers: [ConfirmationService],
  template: `
    <ain-page
      [title]="device()?.deviceType ?? t('iot.device.detail.title')"
      [description]="t('iot.device.detail.description')">
      <div page-actions>
        <a [routerLink]="['/iot/devices']">{{ t('common.back') }}</a>
        @if (device()?.status !== 'Active') {
          <button pButton type="button" [label]="t('common.activate')" (click)="activate()"></button>
        }
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.loadFailed')" [message]="error() ?? ''" />
      } @else if (device()) {
        <section class="card">
          <div class="grid">
            <div><strong>{{ t('iot.devices.fields.model') }}:</strong> {{ device()!.model }}</div>
            <div><strong>{{ t('iot.devices.fields.status') }}:</strong> {{ device()!.status }}</div>
            <div><strong>{{ t('iot.devices.fields.lastSeen') }}:</strong>
              {{ device()!.lastSeenUtc ? (device()!.lastSeenUtc | ainDate:'datetime') : t('common.notAvailable') }}
            </div>
          </div>
        </section>

        <h3>{{ t('iot.command.issueTitle') }}</h3>
        <section class="card">
          <div class="grid">
            <label>
              <span>{{ t('iot.command.fields.type') }}</span>
              <input pInputText [(ngModel)]="commandForm.commandType" />
            </label>
            <label>
              <span>{{ t('iot.command.fields.payload') }}</span>
              <textarea pTextarea rows="3" [(ngModel)]="commandForm.payloadJson"></textarea>
            </label>
          </div>
          <button pButton type="button" [label]="t('iot.command.issueTitle')" [disabled]="issuing()" (click)="issueCommand()"></button>
        </section>

        <h3>{{ t('iot.device.tabs.commands') }}</h3>
        @if (!commands().length) {
          <ain-state type="empty" [title]="t('common.noRecords')" />
        } @else {
          <p-table [value]="commands()" [paginator]="true" [rows]="10">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('iot.command.fields.type') }}</th>
                <th>{{ t('iot.command.fields.status') }}</th>
                <th>{{ t('iot.command.fields.expiresAt') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.commandType }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.expiresAtUtc | ainDate:'datetime' }}</td>
                <td><button pButton type="button" text [label]="t('iot.command.audit.title')" (click)="loadAudit(row)"></button></td>
              </tr>
            </ng-template>
          </p-table>
        }

        @if (auditRows().length) {
          <h4>{{ t('iot.command.audit.title') }}</h4>
          <p-table [value]="auditRows()">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('iot.command.audit.action') }}</th>
                <th>{{ t('iot.command.audit.actor') }}</th>
                <th>{{ t('iot.command.audit.occurredAt') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.action }}</td>
                <td>{{ row.actorUserId ?? t('common.notAvailable') }}</td>
                <td>{{ row.occurredAtUtc | ainDate:'datetime' }}</td>
              </tr>
            </ng-template>
          </p-table>
        }
      }
      <p-confirmDialog />
    </ain-page>
  `,
  styles: [`
    .card { display: grid; gap: 10px; border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; margin-bottom: 12px; }
    .grid { display: grid; gap: 8px; }
    label { display: grid; gap: 6px; }
    a { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; margin-inline-end: 8px; }
  `]
})
export class IoTDeviceDetailComponent implements OnInit {
  private readonly api = inject(IoTApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly localization = inject(LocalizationService);
  private readonly confirmation = inject(ConfirmationService);

  readonly device = signal<IoTDeviceRecord | null>(null);
  readonly commands = signal<IoTDeviceCommandRecord[]>([]);
  readonly auditRows = signal<Array<{ action: string; actorUserId?: string | null; occurredAtUtc: string }>>([]);
  readonly loading = signal(true);
  readonly issuing = signal(false);
  readonly error = signal<string | null>(null);

  commandForm = {
    commandType: 'ping',
    payloadJson: '{}'
  };

  private deviceId = '';

  ngOnInit(): void {
    this.deviceId = this.route.snapshot.paramMap.get('deviceId') ?? '';
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.getDevice(this.deviceId).subscribe({
      next: device => {
        this.device.set(device);
        this.loadCommands();
        this.loading.set(false);
      },
      error: err => {
        this.error.set(err?.message ?? 'load failed');
        this.loading.set(false);
      }
    });
  }

  loadCommands(): void {
    this.api.listCommands(this.deviceId).subscribe({
      next: rows => this.commands.set(rows),
      error: () => this.commands.set([])
    });
  }

  activate(): void {
    this.api.activateDevice(this.deviceId).subscribe({
      next: device => this.device.set(device)
    });
  }

  issueCommand(): void {
    const commandType = this.commandForm.commandType.trim();
    const issue = (confirmed: boolean) => {
      this.issuing.set(true);
      this.api.issueCommand(this.deviceId, {
        commandType,
        payloadJson: this.commandForm.payloadJson,
        confirmed
      }).subscribe({
        next: () => {
          this.issuing.set(false);
          this.loadCommands();
        },
        error: () => this.issuing.set(false)
      });
    };

    if (SENSITIVE_COMMAND_TYPES.has(commandType.toLowerCase())) {
      this.confirmation.confirm({
        header: this.t('iot.command.confirm.title'),
        message: this.t('iot.command.confirm.message'),
        accept: () => issue(true)
      });
      return;
    }

    issue(false);
  }

  loadAudit(row: IoTDeviceCommandRecord): void {
    this.api.listCommandAudit(this.deviceId, row.id).subscribe({
      next: rows => this.auditRows.set(rows)
    });
  }

  t(key: string): string { return this.localization.translate(key); }
}
